import Popover from 'src/components/Popover/Popover';
import * as React from 'react';
import OverlayTrigger, {
  OverlayTriggerProps,
  SharedOverlayTriggerProps,
} from 'src/components/OverlayTrigger/OverlayTrigger';
import { OverlayRenderFn } from 'src/components/OverlayTrigger/types';
import generateGuid from 'src/utils/generate_guid';

/**
 * A component that renders a "trigger" that controls the visibility of a {@link Popover}.
 */
export const PopoverTrigger = React.forwardRef<
  HTMLElement,
  PopoverTriggerProps
>(function PopoverTrigger(props, forwardedRef) {
  const {
    renderPopoverContent,
    renderTrigger: renderTriggerProp,
    disableEscapeKeyDown = false,
    disableAutoFocus,
    PopoverProps: PopoverPropsProp = {},
    ...rest
  } = props;
  const paperId = React.useRef(generateGuid(4, 'popover-paper-'));

  const overlayRenderer = React.useCallback<OverlayRenderFn>(
    (state) => {
      return (
        <Popover
          {...PopoverPropsProp}
          // eslint-disable-next-line deprecation/deprecation
          PaperProps={{
            // eslint-disable-next-line deprecation/deprecation
            ...PopoverPropsProp?.PaperProps,
            id: paperId.current,
          }}
          aria-labelledby={paperId.current}
          anchorEl={state.anchorEl}
          disableAutoFocus={disableAutoFocus}
          open={state.isOpen}
        >
          {renderPopoverContent?.(state)}
        </Popover>
      );
    },
    [PopoverPropsProp, disableAutoFocus, renderPopoverContent]
  );

  if (renderTriggerProp === undefined) {
    return null;
  }

  return (
    <OverlayTrigger
      {...rest}
      ref={forwardedRef}
      PopoverProps={PopoverPropsProp}
      disableAutoFocus={disableAutoFocus}
      disableEscapeKeyDown={disableEscapeKeyDown}
      overlayVariant="popover"
      overlayPopupRole="dialog"
      renderTrigger={renderTriggerProp}
      renderOverlay={overlayRenderer}
    />
  );
});

export type PopoverTriggerProps = Omit<
  SharedOverlayTriggerProps,
  | 'renderOverlay'
  | 'overlayVariant'
  | 'overlayPopupRole'
  | 'triggerPopupRole'
  | 'children'
  | 'modifiers'
  | 'popperOptions'
  | 'popperRef'
  | 'onPlacementUpdate'
  | 'defaultPopperModifierNames'
  | 'PopperProps'
> &
  Pick<OverlayTriggerProps, 'renderTrigger' | 'overlayUseCase'> &
  React.HTMLAttributes<HTMLElement> & {
    /**
     * A render function that returns content to be displayed within a {@link Popover} that will have its
     * visibility controlled by the element returned from {@link renderTrigger}
     * or by the imperative API exposed by the `overlayAction` prop.
     *
     * To customize the props on the {@link Popover} itself, set `PopoverProps`.
     */
    renderPopoverContent?: OverlayRenderFn;
  };

export default PopoverTrigger;
